import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  InputAdornment,
  MenuItem,
  Paper,
  Stack,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  TextField,
  Typography,
} from "@mui/material";
import {
  createQuotableItem,
  deactivateQuotableItem,
  getBranches,
  getItems,
  getQuotableItems,
  QuotableItemPayload,
  setItemPrice,
  setQuotableItemPrice,
  setVatNumberForAllBranches,
  updateBranch,
  updateQuotableItem,
} from "../../../services/catalogueAdmin";
import { useToast } from "../../../hooks/useToast";
import { ifasDisabledBtnSx, ifasTextOrange } from "../ifas/components/OperationsUI";
import { formatR } from "../../../utils/money";

/** A numeric field is dirty only once it differs from the persisted value. */
const isPriceDirty = (edit: string | undefined, original: any) =>
  edit !== undefined && edit.trim() !== "" && Number(edit) !== Number(original ?? 0);

/** A text field is dirty once it differs from the persisted value. */
const isTextDirty = (edit: string | undefined, original: any) =>
  edit !== undefined && edit !== String(original ?? "");

const DIRTY_BG = "#FFFBEB";
const CLEAN_BG = "#FFFFFF";

/** Keeps the row action reachable when the table has to scroll sideways. */
const stickyActionSx = (bg: string) => ({
  position: "sticky" as const,
  right: 0,
  zIndex: 2,
  backgroundColor: bg,
  borderLeft: "1px solid #E5E7EB",
});

const stickyActionHeadSx = {
  position: "sticky" as const,
  right: 0,
  zIndex: 3,
  backgroundColor: "#F9FAFB",
  borderLeft: "1px solid #E5E7EB",
  fontWeight: 800,
};

/** Price inputs: leading R, right-aligned tabular figures. */
const priceInputProps = {
  startAdornment: <InputAdornment position="start">R</InputAdornment>,
};
const priceInputSx = { textAlign: "right" as const, fontVariantNumeric: "tabular-nums" };

/**
 * The shared theme renders `text` buttons white, which is invisible on the white
 * table. Restated locally because theme.ts is shared with claims.
 */
const rowSaveBtnSx = ifasDisabledBtnSx;
const rowEditBtnSx = { fontWeight: 700, color: ifasTextOrange };

/** Marks the dirty row so an unsaved edit is visible at a glance. */
const dirtyRowSx = (dirty: boolean) => ({
  bgcolor: dirty ? DIRTY_BG : undefined,
  "& .MuiTableCell-root:first-of-type": dirty ? { boxShadow: "inset 3px 0 0 #B45309" } : undefined,
});

export default function CataloguePortal() {
  const { showInfoToast } = useToast();
  const [tab, setTab] = useState(0);
  const [loading, setLoading] = useState(false);

  const [items, setItems] = useState<any[]>([]);
  const [branches, setBranches] = useState<any[]>([]);
  const [priceEdits, setPriceEdits] = useState<Record<string, string>>({});
  const [vatEdits, setVatEdits] = useState<Record<string, string>>({});
  const [bulkVat, setBulkVat] = useState("");
  const [bulkVatBusy, setBulkVatBusy] = useState(false);

  // component items
  const [itemClass, setItemClass] = useState(""); // "" = all
  const [compSearch, setCompSearch] = useState("");
  const [components, setComponents] = useState<any[]>([]);
  const [compEdits, setCompEdits] = useState<Record<string, string>>({});
  const [compLoading, setCompLoading] = useState(false);

  const [itemDialog, setItemDialog] = useState<{ open: boolean; edit: any | null }>({ open: false, edit: null });
  const [branchDialog, setBranchDialog] = useState<any | null>(null);

  // Key of the single row-level save in flight, so only that row's button spins.
  const [saving, setSaving] = useState<string | null>(null);
  const savingSpinner = (key: string) =>
    saving === key ? <CircularProgress size={13} color="inherit" /> : undefined;

  const loadCatalogue = useCallback(async () => {
    setLoading(true);
    const [qi, br] = await Promise.all([getQuotableItems(), getBranches()]);
    if (qi?.status === 200) setItems(Array.isArray(qi.data) ? qi.data : []);
    if (br?.status === 200) setBranches(Array.isArray(br.data) ? br.data : []);
    setLoading(false);
  }, []);

  const loadComponents = useCallback(async (cls: string) => {
    setCompLoading(true);
    const res = await getItems(cls || undefined);
    if (res?.status === 200) setComponents(Array.isArray(res.data) ? res.data : []);
    setCompLoading(false);
  }, []);

  useEffect(() => { loadCatalogue(); }, [loadCatalogue]);
  useEffect(() => { if (tab === 1) loadComponents(itemClass); }, [tab, itemClass, loadComponents]);

  const filteredComponents = useMemo(() => {
    const q = compSearch.trim().toLowerCase();
    if (!q) return components;
    return components.filter(
      (c) => String(c.inventoryId).toLowerCase().includes(q) || String(c.description || "").toLowerCase().includes(q),
    );
  }, [components, compSearch]);

  const saveQuotablePrice = async (code: string) => {
    if (saving) return;
    const val = parseFloat(priceEdits[code]);
    if (isNaN(val) || val < 0) { showInfoToast("Enter a valid price."); return; }
    setSaving(`item:${code}`);
    const res = await setQuotableItemPrice(code, val, "Finance price update");
    setSaving(null);
    if (res?.status === 200) {
      showInfoToast(`${code} price updated`);
      // The edit is persisted, so the row is no longer dirty.
      setPriceEdits((p) => { const next = { ...p }; delete next[code]; return next; });
      loadCatalogue();
    } else showInfoToast(res?.data?.message || "Update failed");
  };

  const toggleActive = async (row: any) => {
    if (row.active) {
      const res = await deactivateQuotableItem(row.code);
      if (res?.status === 200) { showInfoToast(`${row.code} deactivated`); loadCatalogue(); }
      else showInfoToast(res?.data?.message || "Failed");
    } else {
      const res = await updateQuotableItem(row.code, { name: row.name, kind: row.kind, basePrice: row.basePrice, taxCode: row.taxCode, description: row.description, active: true });
      if (res?.status === 200) { showInfoToast(`${row.code} reactivated`); loadCatalogue(); }
      else showInfoToast(res?.data?.message || "Failed");
    }
  };

  const saveComponentPrice = async (inventoryId: string) => {
    if (saving) return;
    const val = parseFloat(compEdits[inventoryId]);
    if (isNaN(val) || val < 0) { showInfoToast("Enter a valid price."); return; }
    setSaving(`comp:${inventoryId}`);
    const res = await setItemPrice(inventoryId, val, "Finance price update");
    setSaving(null);
    if (res?.status === 204 || res?.status === 200) {
      showInfoToast(`${inventoryId} price updated`);
      setCompEdits((p) => { const next = { ...p }; delete next[inventoryId]; return next; });
      loadComponents(itemClass);
    } else showInfoToast(res?.data?.message || "Update failed");
  };

  /** Icebolethu invoices under one VAT registration, so this sets every branch at once. */
  const applyVatToAllBranches = async () => {
    const value = bulkVat.trim();
    if (!value) {
      showInfoToast("Enter the VAT registration number first.");
      return;
    }
    if (!window.confirm(`Set VAT number "${value}" on all ${branches.length} branches? This is recorded against each branch in the audit log.`)) {
      return;
    }
    setBulkVatBusy(true);
    const res = await setVatNumberForAllBranches(value);
    setBulkVatBusy(false);
    if (res?.status === 200) {
      showInfoToast(`VAT number applied to ${res.data} branch(es).`);
      setBulkVat("");
      setVatEdits({});
      loadCatalogue();
    } else {
      showInfoToast(res?.data?.message || "Could not apply the VAT number.");
    }
  };

  const saveVat = async (branchId: string) => {
    if (saving) return;
    const val = vatEdits[branchId];
    if (val === undefined) return;
    setSaving(`branch:${branchId}`);
    const res = await updateBranch(branchId, { vatNumber: val });
    setSaving(null);
    if (res?.status === 200) {
      showInfoToast(`${branchId} VAT number updated`);
      setVatEdits((p) => { const next = { ...p }; delete next[branchId]; return next; });
      loadCatalogue();
    } else showInfoToast(res?.data?.message || "Update failed");
  };

  return (
    <Box sx={{ flexGrow: 1, overflow: "auto" }}>
      <Box p={3}>
        <Typography fontSize={22} fontWeight={800} color="#111827" mb={0.5}>Catalogue &amp; Pricing</Typography>
        <Typography fontSize={13} color="#6B7280" mb={2}>Manage funeral packages, extras, component prices and branch billing details</Typography>

        <Paper elevation={0} sx={{ borderRadius: 3, border: "1px solid #E5E7EB", overflow: "hidden" }}>
          <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ borderBottom: "1px solid #E5E7EB", px: 2 }}>
            <Tab label="Packages & Extras" sx={{ fontWeight: 700 }} />
            <Tab label="Component Items" sx={{ fontWeight: 700 }} />
            <Tab label="Branches (VAT / Banking)" sx={{ fontWeight: 700 }} />
          </Tabs>

          <Box p={2.5}>
            {loading ? (
              <Box sx={{ py: 6, textAlign: "center" }}><CircularProgress size={22} /></Box>
            ) : tab === 0 ? (
              <>
                <Stack direction="row" justifyContent="space-between" alignItems="center" mb={1.5}>
                  <Typography fontSize={13} color="#6B7280">Categories (A–D) and named extras a client can be quoted for.</Typography>
                  <Button variant="contained" size="small" onClick={() => setItemDialog({ open: true, edit: null })}>+ New item</Button>
                </Stack>
                <TableContainer sx={{ border: "1px solid #E5E7EB", borderRadius: 2 }}>
                  <Table size="small">
                    <TableHead sx={{ backgroundColor: "#F9FAFB" }}>
                      <TableRow>
                        {["Code", "Name", "Type", "Price", "Tax", "Active"].map((h) => (
                          <TableCell key={h} sx={{ fontWeight: 800, whiteSpace: "nowrap" }}>{h}</TableCell>
                        ))}
                        <TableCell sx={stickyActionHeadSx}>Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {items.map((it) => {
                        const dirty = isPriceDirty(priceEdits[it.code], it.basePrice);
                        const key = `item:${it.code}`;
                        return (
                        <TableRow key={it.code} hover sx={dirtyRowSx(dirty)}>
                          <TableCell sx={{ fontWeight: 700 }}>{it.code}</TableCell>
                          <TableCell>{it.name}</TableCell>
                          <TableCell><Chip size="small" label={it.kind} sx={{ bgcolor: it.kind === "PACKAGE" ? "#FFF4EB" : "#EEF2FF", color: it.kind === "PACKAGE" ? "#C85A00" : "#3730A3", fontWeight: 700 }} /></TableCell>
                          <TableCell>
                            <Stack direction="row" spacing={1} alignItems="center">
                              <Box>
                                <TextField size="small" type="number" defaultValue={it.basePrice}
                                  inputProps={{ style: priceInputSx, "aria-label": `Price for ${it.code}` }}
                                  InputProps={priceInputProps}
                                  onChange={(e) => setPriceEdits((p) => ({ ...p, [it.code]: e.target.value }))}
                                  sx={{ width: 140 }} />
                                {dirty ? (
                                  <Typography fontSize={11} color="#B45309" fontWeight={700} mt={0.25}>
                                    Unsaved — was {formatR(it.basePrice)}
                                  </Typography>
                                ) : null}
                              </Box>
                              <Button size="small" variant="outlined" disabled={!dirty || Boolean(saving)} sx={rowSaveBtnSx}
                                startIcon={savingSpinner(key)}
                                onClick={() => saveQuotablePrice(it.code)}>Save</Button>
                            </Stack>
                          </TableCell>
                          <TableCell>{it.taxCode || "-"}</TableCell>
                          <TableCell>
                            <Chip clickable size="small" label={it.active ? "Active" : "Inactive"} onClick={() => toggleActive(it)}
                              sx={{ bgcolor: it.active ? "#E6F4EC" : "#F3F4F6", color: it.active ? "#1A7F4B" : "#6B7280", fontWeight: 700 }} />
                          </TableCell>
                          <TableCell sx={stickyActionSx(dirty ? DIRTY_BG : CLEAN_BG)}><Button size="small" variant="text" sx={rowEditBtnSx} onClick={() => setItemDialog({ open: true, edit: it })}>Edit</Button></TableCell>
                        </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>
              </>
            ) : tab === 1 ? (
              <>
                <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5} alignItems={{ sm: "center" }} mb={1.5}>
                  <Typography fontSize={13} color="#6B7280">Acumatica component items — set the selling price.</Typography>
                  <Box sx={{ flexGrow: 1 }} />
                  <TextField size="small" label="Search id / description" value={compSearch} onChange={(e) => setCompSearch(e.target.value)} sx={{ width: 260 }} />
                  <TextField select size="small" label="Item class" value={itemClass} onChange={(e) => setItemClass(e.target.value)} sx={{ width: 170 }}>
                    <MenuItem value="">All classes</MenuItem>
                    {["COFFIN", "CASKET", "NON STOCK"].map((c) => <MenuItem key={c} value={c}>{c}</MenuItem>)}
                  </TextField>
                </Stack>
                <Typography fontSize={12} color="#9AA5B1" mb={1}>{compLoading ? "Loading…" : `Showing ${filteredComponents.length} of ${components.length} items`}</Typography>
                <TableContainer sx={{ border: "1px solid #E5E7EB", borderRadius: 2, maxHeight: 560 }}>
                  <Table size="small" stickyHeader>
                    <TableHead>
                      <TableRow>
                        {["Inventory ID", "Description", "Type", "Class", "Price"].map((h) => (
                          <TableCell key={h} sx={{ fontWeight: 800, backgroundColor: "#F9FAFB", whiteSpace: "nowrap" }}>{h}</TableCell>
                        ))}
                        <TableCell sx={stickyActionHeadSx}>Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {compLoading ? (
                        <TableRow><TableCell colSpan={6} align="center" sx={{ py: 4 }}><CircularProgress size={20} /></TableCell></TableRow>
                      ) : filteredComponents.length === 0 ? (
                        <TableRow><TableCell colSpan={6} align="center" sx={{ py: 3, color: "#6B7280" }}>No items match</TableCell></TableRow>
                      ) : filteredComponents.map((c) => {
                        const dirty = isPriceDirty(compEdits[c.inventoryId], c.sellingPrice);
                        const key = `comp:${c.inventoryId}`;
                        return (
                        <TableRow key={c.inventoryId} hover sx={dirtyRowSx(dirty)}>
                          <TableCell sx={{ fontWeight: 700 }}>{c.inventoryId}</TableCell>
                          <TableCell>{c.description}</TableCell>
                          <TableCell>{c.type ? String(c.type).replace(/_/g, " ") : "-"}</TableCell>
                          <TableCell>{c.itemClass || "-"}</TableCell>
                          <TableCell>
                            <TextField size="small" type="number" defaultValue={c.sellingPrice}
                              inputProps={{ style: priceInputSx, "aria-label": `Selling price for ${c.inventoryId}` }}
                              InputProps={priceInputProps}
                              onChange={(e) => setCompEdits((p) => ({ ...p, [c.inventoryId]: e.target.value }))}
                              sx={{ width: 140 }} />
                            {dirty ? (
                              <Typography fontSize={11} color="#B45309" fontWeight={700} mt={0.25}>
                                Unsaved — was {formatR(c.sellingPrice)}
                              </Typography>
                            ) : null}
                          </TableCell>
                          <TableCell sx={stickyActionSx(dirty ? DIRTY_BG : CLEAN_BG)}>
                            <Button size="small" variant="outlined" disabled={!dirty || Boolean(saving)} sx={rowSaveBtnSx}
                              startIcon={savingSpinner(key)}
                              onClick={() => saveComponentPrice(c.inventoryId)}>Save</Button>
                          </TableCell>
                        </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>
              </>
            ) : (
              <>
                <Typography fontSize={13} color="#6B7280" mb={1.5}>Branch billing details used on quotation &amp; tax-invoice letterhead. Edit the VAT number inline, or open Edit for full details.</Typography>
                <Box sx={{ p: 1.5, mb: 2, border: "1px solid #E5E7EB", borderRadius: 1, backgroundColor: "#FAFAFA" }}>
                  <Typography fontSize={13} fontWeight={700} color="#111827">VAT registration number</Typography>
                  <Typography fontSize={12} color="#6B7280" mb={1}>
                    Icebolethu invoices under a single VAT registration, so this applies to every
                    branch at once. Each branch is recorded separately in the audit log.
                  </Typography>
                  <Stack direction={{ xs: "column", sm: "row" }} spacing={1} alignItems={{ sm: "center" }}>
                    <TextField
                      size="small"
                      placeholder="e.g. 4123456789"
                      value={bulkVat}
                      onChange={(e) => setBulkVat(e.target.value)}
                      sx={{ maxWidth: 240 }}
                    />
                    <Button
                      size="small"
                      variant="contained"
                      disabled={bulkVatBusy || !bulkVat.trim()}
                      onClick={applyVatToAllBranches}
                    >
                      {bulkVatBusy ? "Applying…" : `Set for all ${branches.length} branches`}
                    </Button>
                  </Stack>
                </Box>
                <TableContainer sx={{ border: "1px solid #E5E7EB", borderRadius: 2 }}>
                  <Table size="small">
                    <TableHead sx={{ backgroundColor: "#F9FAFB" }}>
                      <TableRow>
                        {["Branch", "Name", "City", "VAT Number", "Email"].map((h) => (
                          <TableCell key={h} sx={{ fontWeight: 800, whiteSpace: "nowrap" }}>{h}</TableCell>
                        ))}
                        <TableCell sx={stickyActionHeadSx}>Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {branches.map((b) => {
                        const dirty = isTextDirty(vatEdits[b.branchId], b.vatNumber || "");
                        const key = `branch:${b.branchId}`;
                        return (
                        <TableRow key={b.branchId} hover sx={dirtyRowSx(dirty)}>
                          <TableCell sx={{ fontWeight: 700 }}>{b.branchId}</TableCell>
                          <TableCell>{b.name}</TableCell>
                          <TableCell>{b.city || "-"}</TableCell>
                          <TableCell>
                            <Stack direction="row" spacing={1} alignItems="center">
                              <Box>
                                <TextField size="small" defaultValue={b.vatNumber || ""}
                                  inputProps={{ "aria-label": `VAT number for ${b.branchId}` }}
                                  onChange={(e) => setVatEdits((p) => ({ ...p, [b.branchId]: e.target.value }))} sx={{ width: 150 }} />
                                {dirty ? (
                                  <Typography fontSize={11} color="#B45309" fontWeight={700} mt={0.25}>Unsaved change</Typography>
                                ) : null}
                              </Box>
                              <Button size="small" variant="outlined" disabled={!dirty || Boolean(saving)} sx={rowSaveBtnSx}
                                startIcon={savingSpinner(key)}
                                onClick={() => saveVat(b.branchId)}>Save</Button>
                            </Stack>
                          </TableCell>
                          <TableCell>{b.email || "-"}</TableCell>
                          <TableCell sx={stickyActionSx(dirty ? DIRTY_BG : CLEAN_BG)}><Button size="small" variant="text" sx={rowEditBtnSx} onClick={() => setBranchDialog({ ...b })}>Edit</Button></TableCell>
                        </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>
              </>
            )}
          </Box>
        </Paper>
      </Box>

      <QuotableItemDialog
        state={itemDialog}
        onClose={() => setItemDialog({ open: false, edit: null })}
        onSaved={() => { setItemDialog({ open: false, edit: null }); loadCatalogue(); }}
        showInfoToast={showInfoToast}
      />
      <BranchDialog branch={branchDialog} onClose={() => setBranchDialog(null)} onSaved={() => { setBranchDialog(null); loadCatalogue(); }} showInfoToast={showInfoToast} />
    </Box>
  );
}

function QuotableItemDialog({ state, onClose, onSaved, showInfoToast }: any) {
  const editing = state.edit;
  const [form, setForm] = useState<QuotableItemPayload>({ code: "", name: "", kind: "EXTRA", basePrice: 0, taxCode: "VAT15" });
  useEffect(() => {
    if (state.open) {
      setForm(editing
        ? { code: editing.code, name: editing.name, kind: editing.kind, basePrice: editing.basePrice, taxCode: editing.taxCode, description: editing.description, active: editing.active }
        : { code: "", name: "", kind: "EXTRA", basePrice: 0, taxCode: "VAT15" });
    }
  }, [state.open, editing]);
  const set = (k: keyof QuotableItemPayload, v: any) => setForm((f) => ({ ...f, [k]: v }));
  const save = async () => {
    if (!form.name) { showInfoToast("Name is required."); return; }
    let res;
    if (editing) res = await updateQuotableItem(editing.code, form);
    else {
      if (!form.code) { showInfoToast("Code is required (e.g. EXTRA-USHERS)."); return; }
      res = await createQuotableItem(form);
    }
    if (res?.status === 200 || res?.status === 201) { showInfoToast(editing ? "Item updated" : "Item created"); onSaved(); }
    else showInfoToast(res?.data?.message || "Save failed");
  };
  return (
    <Dialog open={state.open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>{editing ? `Edit ${editing.code}` : "New quotable item"}</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2} mt={0.5}>
          {!editing ? <TextField label="Code" size="small" value={form.code} onChange={(e) => set("code", e.target.value)} placeholder="EXTRA-USHERS" /> : null}
          <TextField label="Name" size="small" value={form.name} onChange={(e) => set("name", e.target.value)} />
          <TextField select label="Type" size="small" value={form.kind} onChange={(e) => set("kind", e.target.value)}>
            <MenuItem value="PACKAGE">PACKAGE</MenuItem>
            <MenuItem value="EXTRA">EXTRA</MenuItem>
          </TextField>
          <TextField label="Base price" size="small" type="number" value={form.basePrice}
            InputProps={priceInputProps}
            inputProps={{ style: priceInputSx }}
            onChange={(e) => set("basePrice", parseFloat(e.target.value) || 0)} />
          <TextField label="Tax code" size="small" value={form.taxCode} onChange={(e) => set("taxCode", e.target.value)} />
        </Stack>
      </DialogContent>
      <DialogActions><Button onClick={onClose}>Cancel</Button><Button variant="contained" onClick={save}>Save</Button></DialogActions>
    </Dialog>
  );
}

function BranchDialog({ branch, onClose, onSaved, showInfoToast }: any) {
  const [form, setForm] = useState<any>({});
  useEffect(() => { if (branch) setForm({ vatNumber: branch.vatNumber || "", email: branch.email || "", addressLine1: branch.addressLine1 || "", addressLine2: branch.addressLine2 || "", city: branch.city || "", primaryColor: branch.primaryColor || "" }); }, [branch]);
  const set = (k: string, v: any) => setForm((f: any) => ({ ...f, [k]: v }));
  const save = async () => {
    const res = await updateBranch(branch.branchId, form);
    if (res?.status === 200) { showInfoToast(`${branch.branchId} updated`); onSaved(); }
    else showInfoToast(res?.data?.message || "Save failed");
  };
  return (
    <Dialog open={Boolean(branch)} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>Edit {branch?.branchId}</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2} mt={0.5}>
          <TextField label="VAT number" size="small" value={form.vatNumber || ""} onChange={(e) => set("vatNumber", e.target.value)} />
          <TextField label="Email" size="small" value={form.email || ""} onChange={(e) => set("email", e.target.value)} />
          <TextField label="Address line 1" size="small" value={form.addressLine1 || ""} onChange={(e) => set("addressLine1", e.target.value)} />
          <TextField label="Address line 2" size="small" value={form.addressLine2 || ""} onChange={(e) => set("addressLine2", e.target.value)} />
          <TextField label="City" size="small" value={form.city || ""} onChange={(e) => set("city", e.target.value)} />
          <TextField label="Brand colour (#hex)" size="small" value={form.primaryColor || ""} onChange={(e) => set("primaryColor", e.target.value)} />
        </Stack>
      </DialogContent>
      <DialogActions><Button onClick={onClose}>Cancel</Button><Button variant="contained" onClick={save}>Save</Button></DialogActions>
    </Dialog>
  );
}
